import React, { useState } from "react";
import { DialogRoot } from "components/ui/dialog";
import {
  DialogActionTrigger,
  DialogBody,
  DialogCloseTrigger,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@chakra-ui/react";
import { Button } from "components/ui/button";
import api from "utils/apiService";
import { AiOutlineLoading3Quarters } from "react-icons/ai";
import * as XLSX from "xlsx"; // Import XLSX for Excel export
import { toast } from "react-toastify";
import { LuFileSpreadsheet } from "react-icons/lu";
import {
  localizeContractorSkills,
  localizeFacilitySize,
  localizeSupplierTypes,
} from "utils/localization";

const ExportSheet = ({ userRole = "contractor" }) => {
  const [data, setData] = useState({
    data: [],
    pagination: { totalDocs: 0 },
  });
  const [loading, setLoading] = useState(false);
  const [prepared, setPrepared] = useState(false);
  const isSupplier = userRole === "supplier";

  // Fetch data
  const fetchData = async () => {
    setLoading(true);
    try {
      const resource = isSupplier ? "suppliers" : "contractors";
      const res = await api.get(`/users/${resource}?limit=100000`);
      if (res.error) {
        console.error("API Error:", res.error);
        toast.error("خطأ في جلب البيانات");
      } else {
        setData({
          data: res?.data || [],
          pagination: res?.pagination || { totalDocs: 0 },
        });
        toast.success("تم جلب البيانات بنجاح");
        setPrepared(true);
      }
    } catch (error) {
      console.error("Unexpected Error:", error);
      toast.error("حدث خطأ غير متوقع");
    } finally {
      setLoading(false);
    }
  };

  // Export data as Excel
  const exportToExcel = () => {
    if (!data.data.length) {
      toast.info("لا توجد بيانات للتصدير");
      return;
    }

    // Prepare the data for the Excel sheet
    const exportData = data.data.map((item) => {
      const businessInfo = isSupplier ? item.supplierInfo : item.contractorInfo;
      const commonData = {
        الاسم: item.name || "غير متوفر",
        "رقم الهاتف": item.phoneNumber || "غير متوفر",
        الحالة: item.active ? "مفعل" : "غير مفعل",
        "تاريخ التسجيل": item.createdAt || "غير متوفر",
        "الرقم التعريفي": item.id || item._id || "غير متوفر",
        "البريد الإلكتروني": businessInfo?.email || "غير متوفر",
        "اسم المؤسسة": businessInfo?.institutionName || "غير متوفر",
        "السجل التجاري": businessInfo?.commercialRegister || "غير متوفر",
        "حجم المنشأة":
          localizeFacilitySize(businessInfo?.facilitySize) || "غير متوفر",
        "صورة السجل التجاري":
          businessInfo?.commercialRegisterPhoto || "غير متوفر",
        "الصورة الشخصية": item.photo || "غير متوفر",
        "وصف المؤسسة": businessInfo?.description || "غير متوفر",
      };

      if (isSupplier) {
        return {
          ...commonData,
          "الرقم الضريبي": businessInfo?.taxNumber || "غير متوفر",
          "ملف المورد": businessInfo?.profilePdf || "غير متوفر",
          "أنواع التوريد": localizeSupplierTypes(businessInfo?.types),
          المواقع:
            businessInfo?.locations
              ?.map(({ region, city, district }) =>
                [region, city, district].filter(Boolean).join(" - ")
              )
              .join("، ") || "غير متوفر",
        };
      }

      return {
        ...commonData,
        المهارات:
          businessInfo?.skills?.length > 0
            ? localizeContractorSkills(businessInfo.skills)
            : "غير متوفر",
      };
    });

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    const sheetName = isSupplier ? "suppliers" : "contractors";
    XLSX.utils.book_append_sheet(wb, ws, sheetName);

    XLSX.writeFile(wb, `${sheetName}.xlsx`);
    toast.success("تم تصدير البيانات بنجاح");
  };

  return (
    <DialogRoot dir="rtl">
      <DialogTrigger asChild>
        <button
          className="my-4 rounded-md bg-green-500  py-2 px-4 text-white transition-all float-end hover:bg-green-400"
          variant="outline"
          size="sm"
        >
          تصدير إكسل
        </button>
      </DialogTrigger>
      <DialogContent className="fixed top-1/2 -translate-x-1/2 -translate-y-1/2 bg-white text-gray-700 end-1/2 dark:bg-navy-800 dark:text-white">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold">
            تصدير البيانات
          </DialogTitle>
        </DialogHeader>
        <DialogBody>
          {loading ? (
            <div className="flex h-[200px] items-center justify-center rounded-xl bg-white shadow-sm dark:bg-navy-800">
              <AiOutlineLoading3Quarters className="animate-spin text-4xl text-blueSecondary dark:text-white" />
            </div>
          ) : (
            <div className="text-center">
              <div className="flex flex-col items-center justify-center gap-4">
                {!prepared ? (
                  <button
                    className="my-4 rounded-md bg-blueSecondary py-2 px-4 text-white transition-all float-end hover:bg-brandLinear"
                    variant="outline"
                    size="sm"
                    onClick={fetchData}
                  >
                    جلب البيانات
                  </button>
                ) : (
                  <button
                    onClick={exportToExcel}
                    className="my-4 flex flex-col items-center justify-center gap-2 rounded-md bg-green-500 py-2 px-4 text-white transition-all float-end hover:bg-green-400"
                  >
                    <LuFileSpreadsheet className="text-3xl" />
                    <span>تحميل إكسل</span>
                  </button>
                )}
              </div>
            </div>
          )}
        </DialogBody>
        <DialogFooter>
          <DialogActionTrigger asChild>
            <Button variant="outline">
              <span>إلغاء</span>
            </Button>
          </DialogActionTrigger>
        </DialogFooter>
        <DialogCloseTrigger />
      </DialogContent>
    </DialogRoot>
  );
};

export default ExportSheet;
